import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

const testimonials = [
  {
    name: "Rajesh Sharma",
    role: "Operations Director",
    company: "Steelcraft Industries",
    quote:
      "Our response time improved from hours to minutes. Campaign coordination is now faster and cleaner.",
    result: "85% faster response",
  },
  {
    name: "Priya Patel",
    role: "Marketing Director",
    company: "Neotric Group",
    quote:
      "We finally have visibility and control. The platform helps us optimize campaigns based on real outcomes.",
    result: "45% higher engagement",
  },
  {
    name: "Amit Kumar",
    role: "Founder",
    company: "UTL Solutions",
    quote:
      "Automation removed repetitive work from our team. We now focus on growth while workflows run consistently.",
    result: "60% productivity boost",
  },
  {
    name: "Sonia Verma",
    role: "E-commerce Owner",
    company: "Wisechamp Retail",
    quote:
      "Bulk campaigns and follow-ups are easier than ever. We launched quickly and saw conversions in the first week.",
    result: "30% conversion lift",
  },
];

const Testimonials = () => {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const current = testimonials[active];

  return (
    <section
      id="testimonials"
      className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 py-20 text-white sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-300">
            Customer proof
          </p>
          <h2 className="mt-3 text-3xl font-black sm:text-5xl">
            Businesses Trust This Platform
          </h2>
          <p className="mt-4 text-base text-amber-100/80 sm:text-lg">
            Real results from teams using these workflows every day.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          <motion.article
            key={current.name}
            initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-3xl border border-amber-500/20 bg-white/5 p-7 lg:col-span-2"
          >
            <Quote className="h-8 w-8 text-amber-300/70" />
            <p className="mt-4 text-lg leading-relaxed text-amber-100 sm:text-xl">
              "{current.quote}"
            </p>

            <div className="mt-6 flex items-center gap-2 text-amber-300">
              {[...Array(5)].map((_, idx) => (
                <Star key={idx} className="h-4 w-4 fill-current" />
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-amber-500/20 pt-5">
              <div>
                <p className="font-bold text-white">{current.name}</p>
                <p className="text-sm text-amber-100/80">
                  {current.role} - {current.company}
                </p>
              </div>
              <span className="rounded-full bg-amber-400/20 px-3 py-1 text-sm font-semibold text-amber-200">
                {current.result}
              </span>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length)
                }
                className="rounded-lg border border-amber-500/30 p-2 text-amber-200 hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setActive((prev) => (prev + 1) % testimonials.length)}
                className="rounded-lg border border-amber-500/30 p-2 text-amber-200 hover:bg-white/10"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </motion.article>

          <div className="space-y-4">
            {testimonials
              .filter((_, idx) => idx !== active)
              .slice(0, 3)
              .map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => setActive(testimonials.findIndex((t) => t.name === item.name))}
                  className="w-full rounded-2xl border border-amber-500/20 bg-white/5 p-4 text-left hover:bg-white/10"
                >
                  <p className="text-sm font-bold text-white">{item.company}</p>
                  <p className="mt-1 text-xs text-amber-100/80">{item.result}</p>
                </button>
              ))}

            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;